import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import { UserService, ProductService, EventBusService, ProductListItem, ProductSnapshot, HelperService } from "@mypxplat/xplat/core";
import { Product } from "@mypxplat/xplat/core";
import { ProductTileBaseComponent } from "@mypxplat/xplat/features";
import { AppService, AvoService } from "@mypxplat/xplat/web/core";
import { TranslateService } from "@ngx-translate/core";
import { GenericAlertComponent, StudiooneGracePeriodComponent } from "../modals";
import { RuleSet } from "@nativescript/core/ui/styling/css-selector";
import { Router } from "@angular/router";
import { filter, take, takeUntil } from "rxjs/operators";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "myp-product-tile",
  templateUrl: "product-tile.component.html",
  standalone: false,
})
export class ProductTileComponent extends ProductTileBaseComponent implements OnInit {
  @ViewChild("register") registerBtn: ElementRef;
  @Input() product: any;
  @Input() image: string;
  @Input() buttons: Array<any>;
  @Input() title: string;
  @Input() description: string;
  @Input() productLink: string;
  @Input() registerProductTile: boolean;
  @Input() redeemS17Tile: boolean;
  @Input() showDate: boolean = true;
  // Opt-in: render a short, HTML-stripped, 3-line-clamped description under the title
  // (used by the cross-sell rail). Off by default so existing tile usages are unchanged.
  @Input() showStrippedDescription: boolean = false;
  // Opt-in: when set, render a subtle "Buy Now" text link at the bottom that opens this URL
  // in a new tab (used by the cross-sell rail). Empty by default.
  @Input() buyNowUrl: string = "";
  @Output() tileClicked: EventEmitter<any> = new EventEmitter();
  @Output() registerProductClicked: EventEmitter<any> = new EventEmitter();
  @Output() redeems17Clicked: EventEmitter<any> = new EventEmitter();
  public ownedProductID: any;
  public redeeming: boolean = false;
  constructor(
    public userService: UserService,
    public productService: ProductService,
    public appService: AppService,
    public translate: TranslateService,
    public eventBusService: EventBusService,
    public avoService: AvoService,
    public router: Router,
    public helperService: HelperService,
    private _cdr: ChangeDetectorRef
  ) {
    super(userService);
  }

  ngOnInit() {
    if (this.product) {
      if (!this.productLink && this.product.url) {
        this.productLink = this.product.url;
      } else if (!this.productLink && this.product.permalink_products) {
        this.productLink = "https://shop.presonus.com/" + this.product.permalink_products;
      }
      if (!this.productService.productsByStringIDs[this.product.stringId] && this.product.redeemed === false) this.product.redeemable = true;
      this.title = this.product.title;
      this.description = this.product.shortDescription;
      this.image = this.product.imageUrl;
    }

    this.productService.newProductRegistered.subscribe((flag) => {
      if (this.registerBtn && flag) {
        this.registerBtn.nativeElement.focus();
        this.productService.newProductRegistered$.next(false);
      }
    });

    // The unread badge reads userService.userMetadata, which can load after the tile renders.
    this.userService.userMetadataUpdated$.pipe(takeUntil(this.destroy$)).subscribe(() => this._cdr.markForCheck());
  }

  redeemInfo() {
    this.appService.alert(this.translate.instant("products.elligible_to_redeem"));
  }

  showDescriptionModal(e) {
    e.preventDefault();
    e.stopPropagation();
    let modalRef = this.appService.showModal(GenericAlertComponent, { size: "lg" });
    modalRef.componentInstance.title = this.product.title;
    modalRef.componentInstance.html = this.helperService.containsHtml(this.product.description) ? this.product.description : this.helperService.convertLineBreaks(this.product.description);
  }

  redeemProduct() {
    // Fire CTA Clicked for Claim Product (only in My Fender)
    if (this.userService.appId === "fender") {
      this.avoService.avoInitted$
        .pipe(
          filter((v) => !!v),
          take(1)
        )
        .subscribe(() => {
          const te = this.avoService.trackEvent();
          if (te && typeof (te as any).ctaClicked === "function") {
            const page = this.avoService.getPageProperties({ pageName: this.router.url?.indexOf("/home") >= 0 ? "Home" : "Products" });
            const extra: Record<string, any> = {
              ctaText: "Claim Product",
              ctaLocation: this.router.url?.indexOf("/home") >= 0 ? "Home" : "Software PDP",
              userId_: this.user?.["fc-id"] ?? undefined,
              fcId: this.user?.["fc-id"] ?? undefined,
            };
            const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
            (te as any).ctaClicked(payload);
          }
        });
    }
    this.redeeming = true;
    this.productService.redeemProduct({ id: this.product.id, groupId: this.product.groupId }).subscribe((result) => {
      this.productService.getProducts(true).subscribe((result) => {
        this.redeeming = false;
        this._cdr.markForCheck();
        this.tileClicked.next(true);
      });
    });
  }

  tileClick() {
    if (this.product?.redeemable) {
      this.appService.alert(this.translate.instant("products.elligible_to_redeem"));
    } else {
      this.tileClicked.next(true);
    }
  }

  showRedeemS17() {
    const modalRef = this.appService.showModal(StudiooneGracePeriodComponent, {
      size: "lg",
      container: this.appService.appContainer,
    });
    modalRef.componentInstance.asset = this.productService.s16ProductToUpgradeTo7;
  }

  playSample(url, event?: Event) {
    const button = (event?.target as HTMLElement).closest("button");
    if (button?.hasAttribute("data-focus-restored")) {
      button.removeAttribute("data-focus-restored");
      return;
    }
    this.eventBusService.emit(this.eventBusService.types.playAudio, {
      url: url,
      filename: this.product.title,
      triggerElement: button,
    });
  }

  downloadInstaller(product) {
    this.productService.getProductSnapshot(product.id).subscribe((snapshot: ProductSnapshot) => {
      const installer = this.helperService.getInstallerForCurrentOs(snapshot?.softwareDownloads, snapshot?.product?.title, this.appService.os);
      if (installer) {
        this._getTokenizedInstaller(installer);
      } else {
        this.appService.alertError(this.translate.instant("products.no_installers_available"));
      }
    });
  }

  private _getTokenizedInstaller(dl) {
    if (dl.tokenized) {
      this.productService.getTokenizedUrl(dl.folder, dl.filename, "my_fender", dl.title, dl.id.toString(), dl.filename, parseInt(dl.filesize), dl.version || dl.version_number).subscribe({
        next: (result: any) => {
          window.open(result.url);
        },
        error: (error) => {
          this.appService.alertError(error);
        },
      });
    } else {
      window.open(dl.url);
    }
  }
}
